/**
 * Application route paths. Centralising these means renames are a single
 * edit and link helpers stay typed.
 *
 * The post-login app lives under `/app` with named sections. Most
 * sections are still stubs (see `app/routes/coming-soon.tsx`) but the
 * sidebar links resolve so the navigation feels intentional from day
 * one — we'll swap stubs for real pages module-by-module.
 */
export const paths = {
  home: '/',
  oauthCallback: '/oauth/callback',

  // Public auth + signup funnel (Wave 4). `/` is the unified email-first
  // entry; `/signup` and `/login` are the dedicated per-step pages.
  signup: '/signup',
  login: '/login',
  packages: '/packages',
  checkout: '/checkout',
  onboardingExperience: '/onboarding/experience',
  onboardingSource: '/onboarding/source',

  app: '/app',
  dashboard: '/app/dashboard',
  gettingStarted: '/app/getting-started',
  avatars: '/app/avatars',
  // `/app/avatars/new` is the avatar-creation entry screen (design
  // 03b); `avatarTemplates` is the full niche-filtered browser (03a);
  // `avatarWizard` is the describe-your-own step flow.
  avatarNew: '/app/avatars/new',
  avatarTemplates: '/app/avatars/new/templates',
  avatarWizard: '/app/avatars/new/wizard',
  videos: '/app/videos',
  // `/app/videos/:id/qa` renders the full Gemini-powered QA report for
  // a single video. The route is dynamic; use `videoQaPath(id)` to
  // build a typed link.
  videoQa: '/app/videos/:id/qa',
  // `/app/avatars/:id/needs-voice` is the gate shown when a user tries to
  // make a video with an avatar that has no voice connected (the render
  // would otherwise fail with "no ElevenLabs voice_id"). Dynamic route;
  // use `avatarNeedsVoicePath(id)` to build a typed link.
  avatarNeedsVoice: '/app/avatars/:id/needs-voice',
  generate: '/app/generate',
  copyReel: '/app/copy-reel',
  /**
   * Scene generator route. The route itself is `/app/scenes/new`; the
   * concrete avatar is passed as a `?avatar=<id>` query param (matches
   * the existing `?avatar=` pattern on `paths.videos` and
   * `paths.generate`). Use `sceneNewFor(id)` to build a link.
   */
  sceneNew: '/app/scenes/new',
  followed: '/app/followed',
  // The cross-account reel browser (table) replaced the old
  // `/app/analytics` general dashboard. The per-account drill-down lives
  // at `followAccountDetails/:id` (was `/app/analytics?account_id=`).
  reelAnalytics: '/app/reel-analytics',
  followAccountDetails: '/app/follow-account-details',
  posts: '/app/posts',
  automation: '/app/automation',
  campaign: '/app/campaign',
  ideation: '/app/ideation',
  settings: '/app/settings',
  billing: '/app/billing',
  help: '/app/help',
} as const;

export type Path = (typeof paths)[keyof typeof paths];

/**
 * Build a link to the scene generator for a specific avatar. The
 * `avatar=<id>` query param is the route's "which avatar are we
 * generating for?" channel; without it the page falls back to a
 * "pick an avatar" empty state.
 */
export function sceneNewFor(
  avatarId: number,
  mode?: 'describe' | 'clone' | 'templates' | 'upload',
  intent?: 'scene' | 'image',
): string {
  const params = new URLSearchParams({ avatar: String(avatarId) });
  if (mode) params.set('mode', mode);
  // `intent=image` makes the generator save approved previews as
  // `kind="image"` (general avatar photo, no HeyGen sync) instead of
  // the default `kind="scene"`. The two flows are otherwise identical —
  // same lanes, same generate/approve calls — so the only divergence is
  // this query flag and the saved row's `kind`. Omitted for scenes so
  // existing scene links stay byte-identical.
  if (intent === 'image') params.set('intent', 'image');
  return `${paths.sceneNew}?${params.toString()}`;
}

/**
 * Build the detail (reel) path for a video (`/app/videos/:id`). This is
 * the single source of truth for the video-detail link, used by the
 * Videos library list and the dashboard Recent Videos strip so the two
 * never drift.
 */
export function videoDetailPath(videoId: number): string {
  return `${paths.videos}/${videoId}`;
}

/** Build the QA report path for a video. */
export function videoQaPath(videoId: number): string {
  return `${paths.videos}/${videoId}/qa`;
}

/** Build the "avatar needs a voice" gate path for an avatar. */
export function avatarNeedsVoicePath(avatarId: number): string {
  return `${paths.avatars}/${avatarId}/needs-voice`;
}

/**
 * Tabs the avatar detail page can deep-link to via `?tab=<id>`. About is
 * the default section and is represented by the ABSENCE of the param, so
 * it is intentionally not part of this union.
 */
export type AvatarDetailTab =
  | 'scenes'
  | 'images'
  | 'reference'
  | 'voice'
  | 'videos'
  | 'activity'
  | 'settings';

/**
 * Build the avatar detail path (`/app/avatars/:id`), optionally deep-
 * linking to a specific tab via `?tab=<id>`. The avatar-detail page reads
 * `?tab=` on mount to select its active section. Used by the scene
 * generator's post-save redirect so a saved batch lands on the avatar's
 * Scenes (or Images) tab and jumps to that section.
 */
export function avatarDetailPath(avatarId: number, tab?: AvatarDetailTab): string {
  const base = `${paths.avatars}/${avatarId}`;
  return tab ? `${base}?tab=${tab}` : base;
}

/**
 * Build a link into the video wizard that prefills the avatar + script
 * from an existing video (the "Generate similar" affordance on the
 * detail page). The wizard reads `?from=<id>` on mount, fetches the
 * source video, and seeds the store · the user can then tweak any
 * field before rendering. No backend mutation happens for this path;
 * the original video stays untouched.
 */
export function generateFromVideo(videoId: number): string {
  const params = new URLSearchParams({ from: String(videoId) });
  return `${paths.generate}?${params.toString()}`;
}

/**
 * Build a link into the video wizard that seeds the script from a
 * Copy-From-Reel ingest record (the "Send to video wizard" affordance
 * on `/app/copy-reel`). The wizard reads `?from-ingest=<id>` on mount,
 * resolves the ingest row, and seeds the draft script + source. No
 * backend mutation happens for this path; the ingest row stays
 * untouched and the user still picks an avatar in the wizard.
 */
export function generateFromIngest(ingestId: number): string {
  const params = new URLSearchParams({ 'from-ingest': String(ingestId) });
  return `${paths.generate}?${params.toString()}`;
}

/** Build a link to the per-account reel drill-down (`/app/follow-account-details/:id`). */
export function followAccountDetailsFor(accountId: number): string {
  return `${paths.followAccountDetails}/${accountId}`;
}
